import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from '@angular/core';
import { FeeWaiverDatesHelper, WaiversHelper } from '@features/groups/fee-waivers/_core/helpers';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TimezoneHelper } from '@shared/helpers';
import { FeeWaiver, FeeWaiverDateInterval, FeeWaiverStatusEnum } from '@shared/types/waivers';
import { DatepickerComponent } from '@ui-kit/form';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'fee-waiver-datepicker',
    imports: [DatepickerComponent, TranslateModule, TooltipModule],
    templateUrl: './fee-waiver-datepicker.component.html',
    styleUrl: './fee-waiver-datepicker.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class FeeWaiverDatepickerComponent {
    private translate = inject(TranslateService);

    id = input.required<string>();
    feeWaiver = model.required<FeeWaiver>();
    disabled = input.required<boolean>();

    feeWaiverStatus = input<FeeWaiverStatusEnum | null>(null);
    endDatePlaceholder = input<string>('');

    displayIntervalInline = input<boolean>(true);
    displayIntervalInTooltip = input<boolean>(false);

    isInvalid = computed(() => this.computeIsInvalid());
    feeWaiverInterval = computed(() => this.computeIntervalDisplayValue());

    feeWaiverIntervalInfo = computed(() => {
        const p = this.feeWaiverInterval();

        if (!p) {
            return '';
        }
        const { key, params } = WaiversHelper.getFeeWaiverIntervalPeriodInfoTranslation(p);
        return this.translate.instant(key, params);
    });

    readonly startId = computed(
        () =>
            `${this.id()}-start-date-${
                this.feeWaiver().billingFeeWaiverGroupId
                    ? this.feeWaiver().billingFeeWaiverGroupId
                    : this.feeWaiver().feeWaiverId
            }`
    );
    readonly endId = computed(
        () =>
            `${this.id()}-end-date-${
                this.feeWaiver().billingFeeWaiverGroupId
                    ? this.feeWaiver().billingFeeWaiverGroupId
                    : this.feeWaiver().feeWaiverId
            }`
    );
    readonly waiverWithMonthInPast = computed(() => FeeWaiverDatesHelper.isOngoingWaiver(this.feeWaiver().startDate));

    readonly firstDayOfCurrentMonth = TimezoneHelper.firstDayOfCurrentUTCMonth();
    readonly lastMonthDate = TimezoneHelper.firstDayOfPreviousMonth(TimezoneHelper.firstDayOfCurrentUTCMonth());

    readonly feeWaiverStatusEnum = FeeWaiverStatusEnum;

    onChange = output<{ isInvalid: boolean; waiver: FeeWaiver }>();

    constructor() {}

    handleDateChange(): void {
        this.feeWaiver.update(waiver => ({ ...waiver }));
        this.onChange.emit({ isInvalid: this.isInvalid(), waiver: this.feeWaiver() });
    }

    private computeIntervalDisplayValue(): FeeWaiverDateInterval | null {
        const interval = this.computeDateInterval();

        if (!interval || this.isInvalid()) {
            return null;
        }

        return FeeWaiverDatesHelper.buildFeeWaiverDateInterval(interval.startDate, interval.endDate);
    }

    private computeIsInvalid(): boolean {
        const interval = this.computeDateInterval();

        if (!interval) {
            return false;
        }

        return interval.startDate > interval.endDate;
    }

    private computeDateInterval(): { startDate: Date; endDate: Date } | null {
        const startD = this.feeWaiver().startDate;
        const endD = this.feeWaiver().endDate;

        if (!startD || !endD) {
            return null;
        }

        const startDate = TimezoneHelper.firstDayOfMonth(startD);
        const endDate = TimezoneHelper.firstDayOfMonth(endD);

        return { startDate, endDate };
    }
}
